import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { checkEventLimit, requestEventLimitOverride } from "../services/createEventService";
import { useAuth } from "../context/AuthContext";
import { hasDraft, clearDraft, resolveDraftUserKey } from "../utils/createEventDraft";

/**
 * Gates navigation to /events/create behind Events/checkEventLimit, then —
 * only once the limit check passes — offers to resume or discard a saved
 * draft. Mirrors the mobile app's onCreateEvent handler in
 * Component/modals/create-event-for-promoter/index.js exactly, including
 * the order (limit check first, draft check second).
 */
export const useCreateEventGate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checking, setChecking] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null); // { message, eventType } | null
  const [sendingRequest, setSendingRequest] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(false); // true once a draft was found and needs a decision

  const openCreateEvent = useCallback(
    async (eventType = "Public") => {
      setChecking(true);
      try {
        const res = await checkEventLimit(eventType);
        if (res.data?.Result === "EventLimitReached") {
          setLimitInfo({ message: res.data?.Message, eventType });
          return;
        }
        if (hasDraft(resolveDraftUserKey(user))) {
          setDraftPrompt(true);
          return;
        }
        navigate("/events/create");
      } catch (err) {
        const data = err.response?.data;
        if (data?.Result === "EventLimitReached") {
          setLimitInfo({ message: data?.Message, eventType });
        } else {
          toast.error("Couldn't check your event limit. Please try again.");
        }
      } finally {
        setChecking(false);
      }
    },
    [navigate, user]
  );

  const closeLimitModal = () => setLimitInfo(null);

  const sendLimitRequest = async () => {
    if (!limitInfo) return;
    setSendingRequest(true);
    try {
      await requestEventLimitOverride(limitInfo.eventType);
      toast.success("Request sent to your admin.");
      setLimitInfo(null);
    } catch {
      toast.error("Failed to send request.");
    } finally {
      setSendingRequest(false);
    }
  };

  const resumeDraft = () => {
    setDraftPrompt(false);
    navigate("/events/create");
  };

  const discardDraftAndCreate = () => {
    clearDraft(resolveDraftUserKey(user));
    setDraftPrompt(false);
    navigate("/events/create");
  };

  const cancelDraftPrompt = () => setDraftPrompt(false);

  return {
    checking,
    openCreateEvent,
    limitInfo,
    closeLimitModal,
    sendLimitRequest,
    sendingRequest,
    draftPrompt,
    resumeDraft,
    discardDraftAndCreate,
    cancelDraftPrompt,
  };
};
